import { createSlice } from '@reduxjs/toolkit';
import BookingError from '../enums/bookingError';
import BookingStatus from '../enums/bookingStatus';
import postScheduleTour from '../apis/postScheduleTour';
import postSelfGuidedTourLead from '../apis/postSelfGuidedTourLead';
import {
  selectAdditionalHomes,
  selectBookingStatus,
  selectQuestionResponses,
  selectSelectedSlot,
  selectSpecId,
  selectVerificationMethod
} from './selectors';
import initialState from './initialState';
import ModalStep from '../enums/modalStep';

const selfGuidedTourSlice = createSlice({
  name: 'selfGuidedTour',
  initialState,
  reducers: {
    setSpecId: (state, action) => {
      state.specId = action.payload.specId;
    },
    setAdditionalHomes: (state, action) => {
      state.additionalHomes = action.payload.additionalHomes;
    },
    setSelectedSlot: (state, action) => {
      if (state.modalStep) {
        // only allow selecting a slot if the modal is open
        state.selectedSlot = action.payload.selectedSlot;
      }
    },
    setQuestionsResponses: (state, action) => {
      state.questionsResponses = action.payload.questionsResponses;
    },
    createModal: (state, action) => {
      state.specId = action.payload.specId;
      state.modalStep = action.payload.modalStep
        ? action.payload.modalStep
        : ModalStep.DateTimeSelection;
    },
    setModalStep: (state, action) => {
      state.modalStep = action.payload.modalStep;
    },
    bookingReset: state => {
      state.additionalHomes = [];
      state.booking = null;
      state.bookingError = null;
      state.bookingStatus = BookingStatus.Pending;
      state.modalStep = ModalStep.DateTimeSelection;
      state.questionsResponses = [];
      state.selectedSlot = null;
      state.verificationMethod = null;
    },
    closeModal: state => {
      state.additionalHomes = [];
      state.booking = null;
      state.bookingError = null;
      state.bookingStatus = BookingStatus.Pending;
      state.modalStep = null;
      state.questionsResponses = [];
      state.selectedSlot = null;
      state.verificationMethod = null;
    },
    /**
     * Sets the booking object and status in the state.
     * @param state
     * @param action
     * @param action.payload.booking - The booking object to set.
     * @param {BookingStatus} [action.payload.status] - (Optional) The booking status to set.
     */
    setBooking: (state, action) => {
      state.booking = action.payload.booking;
      if (action.payload.status) {
        state.bookingStatus = action.payload.status;
      }
    },
    /**
     * Sets the booking status in the state.
     * @param state
     * @param action
     * @param {BookingStatus} action.payload.status - The booking status to set.
     */
    setBookingStatus: (state, action) => {
      state.bookingStatus = action.payload.status;
    },
    setBookingError: (state, action) => {
      state.bookingError = action.payload.bookingError;
    },
    setVerificationMethod: (state, action) => {
      state.verificationMethod = action.payload.verificationMethod;
    },
    setTourableSpecs: (state, action) => {
      state.tourableSpecs = action.payload;
    }
  }
});

// Exporting the actions
export const {
  setSpecId,
  setAdditionalHomes,
  setSelectedSlot,
  setQuestionsResponses,
  createModal,
  bookingReset,
  setModalStep,
  closeModal,
  setBooking,
  setBookingStatus,
  setBookingError,
  setVerificationMethod
} = selfGuidedTourSlice.actions;

/**
 * Action (thunk) to schedule a self-guided tour.
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const scheduleTour = () => async (dispatch, getState) => {
  const state = getState();
  const user = JSON.parse(localStorage.getItem('selfGuidedSession'));
  let bookingStatus = selectBookingStatus(state);

  // if the booking status is failed, we should not try to schedule a new tour
  // until they reset and the process starts over
  if (bookingStatus === BookingStatus.Failed) {
    return;
  }

  const selectedSlot = selectSelectedSlot(state);
  const additionalHomes = selectAdditionalHomes(state);
  const verificationMethod = selectVerificationMethod(state);
  const questionResponses = selectQuestionResponses(state);
  const specId = selectSpecId(state);
  const specs = [specId, ...additionalHomes];
  const date = selectedSlot.UtcDate.replace('Z', '');
  // ensure that the booking error is reset before trying scheduling a new tour
  dispatch(setBookingError({ bookingError: null }));

  try {
    const booking = await postScheduleTour(
      user,
      specs,
      date,
      verificationMethod,
      questionResponses
    );
    bookingStatus = BookingStatus.Scheduled;
    dispatch(setBooking({ booking, status: bookingStatus }));
    dispatch(setModalStep({ modalStep: ModalStep.Confirmation }));
  } catch (error) {
    dispatch(
      setBookingError({ bookingError: BookingError.fromKey(error.message) })
    );
    bookingStatus = BookingStatus.Failed;
    dispatch(setBookingStatus({ status: bookingStatus }));
  }

  // Post the lead after successfully scheduling the tour
  if (bookingStatus === BookingStatus.Scheduled) {
    postSelfGuidedTourLead(user, specs, selectedSlot);
  }
};

// Exporting the whole slice
export default selfGuidedTourSlice;
